import { Check, Sun, Moon, Monitor, Building2, User as UserIcon, X, ImageIcon, Sparkles, Wand2 } from "lucide-react";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import { useColorTheme, COLOR_THEMES } from "../../hooks/useColorTheme";
import { useLogoTint } from "../../hooks/useLogoTint";
import { useRestaurant } from "../../context/RestaurantContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";

const HEX_RE = /^#[0-9a-f]{6}$/i;

/**
 * ColorThemeSection — settings page section to configure both:
 *   1. Light/Dark/System mode (via next-themes)
 *   2. Color palette scope: user default vs current restaurant
 *   3. Color palette (6 presets + "Personalizado" with hex picker)
 *   4. Logo preview (read from currentRestaurant.logo_url)
 *
 * The scope selector lets a manager use Cobre at a steakhouse and Esmeralda
 * at a vegan café in the same browser without re-configuring on each switch.
 */
export function ColorThemeSection() {
  const { currentRestaurant } = useRestaurant();
  const restaurantId = currentRestaurant?.restaurant_id || null;
  const restaurantName = currentRestaurant?.name || "este restaurante";
  const logoUrl = currentRestaurant?.logo_url || null;

  const {
    theme: activeTheme,
    accent: activeAccent,
    userTheme,
    restaurantTheme,
    restaurantAccent,
    hasRestaurantOverride,
    setUserTheme,
    setRestaurantTheme,
    clearRestaurantTheme,
  } = useColorTheme(restaurantId);

  const { theme: mode, setTheme: setMode, resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [logoTintEnabled, toggleLogoTint] = useLogoTint();

  // Scope mirrors actual storage state. Custom is always per-restaurant.
  const [scope, setScope] = useState(hasRestaurantOverride ? "restaurant" : "user");

  // Hex input local state (synced with actual accent if any)
  const [hexInput, setHexInput] = useState(restaurantAccent || "#7C3AED");

  useEffect(() => setMounted(true), []);
  useEffect(() => {
    setScope(hasRestaurantOverride ? "restaurant" : "user");
  }, [hasRestaurantOverride]);
  useEffect(() => {
    if (restaurantAccent) setHexInput(restaurantAccent);
  }, [restaurantAccent]);

  const MODES = [
    { id: "light", label: "Claro", icon: Sun },
    { id: "dark", label: "Oscuro", icon: Moon },
    { id: "system", label: "Sistema", icon: Monitor },
  ];

  const editingTheme = scope === "restaurant"
    ? (restaurantTheme || userTheme)
    : userTheme;

  const handleSwatchClick = (id) => {
    if (id === "custom") {
      // Custom requires a hex; force restaurant scope
      if (!restaurantId) return;
      setScope("restaurant");
      const hex = HEX_RE.test(hexInput) ? hexInput : "#7C3AED";
      setRestaurantTheme("custom", hex, restaurantId);
      return;
    }
    if (scope === "restaurant" && restaurantId) {
      setRestaurantTheme(id, null, restaurantId);
    } else {
      setUserTheme(id);
    }
  };

  const handleHexChange = (e) => {
    const v = e.target.value;
    setHexInput(v);
    if (HEX_RE.test(v) && restaurantId) {
      setRestaurantTheme("custom", v.toLowerCase(), restaurantId);
    }
  };

  const handleClearOverride = () => {
    clearRestaurantTheme(restaurantId);
    setScope("user");
  };

  const isCustomActive = editingTheme === "custom";

  return (
    <Card data-testid="color-theme-section">
      <CardHeader>
        <CardTitle className="font-heading text-lg">Apariencia</CardTitle>
        <CardDescription>
          Personaliza el modo claro/oscuro, la paleta de color y la identidad visual de
          cada restaurante. Los cambios se sincronizan en todos tus dispositivos.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* ── Logo preview ── */}
        {restaurantId && (
          <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-3" data-testid="branding-logo-preview">
            <div className="flex items-center gap-4">
              <div
                className="app-logo-glow w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden bg-card border border-border"
                data-testid="branding-logo-thumb"
              >
                {logoUrl ? (
                  <img src={logoUrl} alt={`Logo ${restaurantName}`} className="w-full h-full object-contain" />
                ) : (
                  <ImageIcon className="w-6 h-6 text-muted-foreground/60" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-foreground truncate">{restaurantName}</div>
                <div className="text-xs text-muted-foreground">
                  {logoUrl ? "Logo activo · usado en web, PDFs y carta" : "Sin logo · sube uno desde Mis restaurantes → Branding"}
                </div>
              </div>
            </div>
            {logoUrl && (
              <label className="flex items-start gap-2.5 cursor-pointer pt-2 border-t border-border" data-testid="logo-tint-toggle-label">
                <Wand2 className="w-3.5 h-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium text-foreground">Resaltar logo con color de tema</div>
                  <p className="text-[11px] text-muted-foreground leading-snug">
                    Añade un sutil aro y halo del color del tema activo alrededor del logo. No altera la imagen.
                  </p>
                </div>
                <Switch
                  checked={logoTintEnabled}
                  onCheckedChange={toggleLogoTint}
                  className="ml-auto compact-btn"
                  data-testid="logo-tint-switch"
                />
              </label>
            )}
          </div>
        )}

        {/* ── Mode (light / dark / system) ── */}
        <div className="space-y-3">
          <div className="text-sm font-medium text-foreground">Modo de visualización</div>
          <div className="grid grid-cols-3 gap-2">
            {MODES.map((m) => {
              const Icon = m.icon;
              const isActive = mounted && mode === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setMode(m.id)}
                  className={`flex flex-col items-center justify-center gap-2 rounded-xl border p-4 transition-colors ${
                    isActive
                      ? "border-primary bg-primary/5 ring-1 ring-primary"
                      : "border-border bg-card hover:bg-muted/50"
                  }`}
                  data-testid={`mode-card-${m.id}`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? "text-primary" : "text-muted-foreground"}`} />
                  <span className={`text-sm font-medium ${isActive ? "text-foreground" : "text-muted-foreground"}`}>
                    {m.label}
                  </span>
                </button>
              );
            })}
          </div>
          {mounted && (
            <p className="text-xs text-muted-foreground">
              Activo: <span className="text-foreground font-medium">{resolvedTheme === "dark" ? "Oscuro" : "Claro"}</span>
              {mode === "system" && " (siguiendo la preferencia del sistema)"}
            </p>
          )}
        </div>

        {/* ── Scope selector ── */}
        {restaurantId && (
          <div className="space-y-2">
            <div className="text-sm font-medium text-foreground">Aplicar el cambio de paleta a</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setScope("user")}
                className={`flex items-start gap-2 rounded-xl border p-3 text-left transition-colors ${
                  scope === "user"
                    ? "border-primary bg-primary/5 ring-1 ring-primary"
                    : "border-border bg-card hover:bg-muted/50"
                }`}
                data-testid="scope-user-card"
              >
                <UserIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${scope === "user" ? "text-primary" : "text-muted-foreground"}`} />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground">Mi cuenta</div>
                  <div className="text-[11px] text-muted-foreground leading-snug">
                    Aplica a todos mis restaurantes (que no tengan tema propio).
                  </div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setScope("restaurant")}
                className={`flex items-start gap-2 rounded-xl border p-3 text-left transition-colors ${
                  scope === "restaurant"
                    ? "border-primary bg-primary/5 ring-1 ring-primary"
                    : "border-border bg-card hover:bg-muted/50"
                }`}
                data-testid="scope-restaurant-card"
              >
                <Building2 className={`w-4 h-4 mt-0.5 flex-shrink-0 ${scope === "restaurant" ? "text-primary" : "text-muted-foreground"}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-foreground truncate">
                    Solo {restaurantName}
                  </div>
                  <div className="text-[11px] text-muted-foreground leading-snug">
                    Solo este local. Otros restaurantes no se afectan.
                  </div>
                </div>
              </button>
            </div>
            {hasRestaurantOverride && (
              <div className="flex items-center justify-between rounded-lg bg-muted/40 px-3 py-2">
                <span className="text-xs text-muted-foreground">
                  <span className="text-foreground font-medium">{restaurantName}</span> tiene un tema personalizado.
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearOverride}
                  className="h-7 px-2 text-xs gap-1 compact-btn"
                  data-testid="clear-restaurant-theme-btn"
                >
                  <X className="w-3 h-3" />
                  Quitar
                </Button>
              </div>
            )}
          </div>
        )}

        {/* ── Color palette ── */}
        <div className="space-y-3">
          <div>
            <div className="text-sm font-medium text-foreground">Paleta de color</div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {scope === "restaurant"
                ? `Cambiará solo en ${restaurantName}.`
                : "Cambiará en todos tus restaurantes (los que no tengan tema propio)."}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {COLOR_THEMES.map((t) => {
              const isActive = t.id === editingTheme;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => handleSwatchClick(t.id)}
                  className={`group relative flex items-center gap-3 rounded-xl border p-3 transition-colors text-left ${
                    isActive
                      ? "border-primary bg-primary/5 ring-1 ring-primary"
                      : "border-border bg-card hover:bg-muted/50"
                  }`}
                  data-testid={`color-theme-card-${t.id}`}
                >
                  <span
                    className="relative w-10 h-10 rounded-lg flex-shrink-0 shadow-sm"
                    style={{
                      background: `linear-gradient(135deg, ${t.swatch} 0%, ${t.swatchDark} 100%)`,
                    }}
                  >
                    {isActive && (
                      <Check className="absolute inset-0 m-auto w-5 h-5 text-white drop-shadow" strokeWidth={3} />
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-foreground">
                      {t.name}
                    </div>
                    <div className="text-[11px] text-muted-foreground leading-snug">
                      {t.description}
                    </div>
                  </div>
                </button>
              );
            })}

            {/* Custom (hex picker) — only meaningful per-restaurant */}
            {restaurantId && (
              <button
                type="button"
                onClick={() => handleSwatchClick("custom")}
                className={`group relative flex items-center gap-3 rounded-xl border p-3 transition-colors text-left ${
                  isCustomActive
                    ? "border-primary bg-primary/5 ring-1 ring-primary"
                    : "border-border bg-card hover:bg-muted/50"
                }`}
                data-testid="color-theme-card-custom"
              >
                <span
                  className="relative w-10 h-10 rounded-lg flex-shrink-0 shadow-sm border border-border"
                  style={{
                    background: isCustomActive
                      ? hexInput
                      : "conic-gradient(from 0deg, #ef4444, #f59e0b, #10b981, #06b6d4, #8b5cf6, #ec4899, #ef4444)",
                  }}
                >
                  {isCustomActive ? (
                    <Check className="absolute inset-0 m-auto w-5 h-5 text-white drop-shadow" strokeWidth={3} />
                  ) : (
                    <Sparkles className="absolute inset-0 m-auto w-4 h-4 text-white drop-shadow" />
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-foreground">Personalizado</div>
                  <div className="text-[11px] text-muted-foreground leading-snug">
                    {isCustomActive ? `Tu color: ${(activeAccent || hexInput).toUpperCase()}` : "Tu hex exacto"}
                  </div>
                </div>
              </button>
            )}
          </div>

          {/* Hex picker — only visible when custom is active */}
          {isCustomActive && restaurantId && (
            <div className="flex items-center gap-2 rounded-xl border border-border bg-muted/30 p-3" data-testid="custom-hex-picker">
              <label htmlFor="custom-hex-color" className="flex items-center gap-2 cursor-pointer flex-shrink-0">
                <input
                  id="custom-hex-color"
                  type="color"
                  value={HEX_RE.test(hexInput) ? hexInput : "#7c3aed"}
                  onChange={handleHexChange}
                  className="w-10 h-10 rounded-lg cursor-pointer border border-border bg-card"
                  data-testid="custom-hex-input-color"
                />
              </label>
              <Input
                value={hexInput}
                onChange={handleHexChange}
                placeholder="#7C3AED"
                maxLength={7}
                className="font-mono text-sm uppercase max-w-[160px]"
                data-testid="custom-hex-input-text"
              />
              <p className="text-[11px] text-muted-foreground leading-snug flex-1 min-w-0">
                Introduce el hex exacto del color de tu marca. Se ajusta automáticamente para modo oscuro.
              </p>
            </div>
          )}

          {hasRestaurantOverride && scope === "user" && activeTheme !== userTheme && (
            <p className="text-[11px] text-muted-foreground">
              Nota: tu cuenta usa <span className="text-foreground font-medium">{COLOR_THEMES.find((c) => c.id === userTheme)?.name}</span>, pero ahora ves <span className="text-foreground font-medium">{activeTheme === "custom" ? "Personalizado" : COLOR_THEMES.find((c) => c.id === activeTheme)?.name}</span> porque {restaurantName} tiene tema propio.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default ColorThemeSection;
